import { Maybe } from '@csp/csp-common-model';
import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { useBarcodeInputContext } from '../context/barcode-input-context/useBarcodeInputContext';
import { useStudyContext } from '../context/study/useStudyContext';
import { BarcodeType } from '../model/BarcodeType';
import { StaticContentService } from '../service/StaticContentService';
import { useBarcodeScanningDetection } from './useBarcodeScanningDetection';
import { useIsBrowserTabActive } from './useIsBrowserTabActive';

type BarcodeFormatConfig = {
  validate(code: string): Maybe<string>;
  onDetected(code: string, type: BarcodeType): void;
};

const DEFAULT_BARCODE_TYPES = Object.keys(BarcodeType).map(key => key as BarcodeType);

type In = {
  barcodeTypes?: BarcodeType[];
  onDetected: (code: string, type: BarcodeType) => void;
  ignoreBlocking?: boolean;
  isDisabled?: boolean;
};

type Out = {
  handleOnChange(event: ChangeEvent<HTMLInputElement>): void;
  handleOnTextFieldKeydown(event: KeyboardEvent<HTMLInputElement>): void;
  submitTextField(): void;
  textFieldValue: string;
  error: Maybe<string>;
  isScanningAvailable: boolean;
  isValid: boolean;
};

export const useBarcodeInput = ({
  barcodeTypes = DEFAULT_BARCODE_TYPES,
  onDetected,
  ignoreBlocking = false,
  isDisabled = false,
}: In): Out => {
  const isBrowserTabActive = useIsBrowserTabActive();
  const [textFieldValue, setTextFieldValue] = useState<string>('');
  const [error, setError] = useState<Maybe<string>>();

  const { isBarcodeInputBlocked } = useBarcodeInputContext();

  const barcodeInputTexts = StaticContentService.getGenericEntry('webHcpBarcodeInput');
  const hbsInputErrorTexts = StaticContentService.getGenericEntry('webHcpHbsInputError');

  const {
    studyConfig: { hbsConfig },
  } = useStudyContext();

  const barcodeFormatConfigByType: Record<BarcodeType, BarcodeFormatConfig> = {
    [BarcodeType.HBS_KIT_CODE]: {
      validate: code =>
        hbsConfig.bioSampleCodeFormatter.validateBioSampleKitCode(code, hbsInputErrorTexts.sampleKitCodeInputError.enum)
          ?.message,
      onDetected,
    },
    [BarcodeType.HBS_SAMPLE_CODE]: {
      validate: code =>
        hbsConfig.bioSampleCodeFormatter.validateBioSampleCode(code, hbsInputErrorTexts.sampleCodeInputError.enum)
          ?.message,
      onDetected,
    },
  };

  const handleOnSubmit = (code: string): void => {
    barcodeTypes.forEach(type => {
      const { validate, onDetected } = barcodeFormatConfigByType[type];
      const validationErrorMessage = validate(code);
      if (!validationErrorMessage) {
        clear();
        onDetected(code, type);
        return;
      }
    });
  };

  const isScanningDisabled = isDisabled || (!ignoreBlocking && isBarcodeInputBlocked);

  useBarcodeScanningDetection({
    onBarcodeIdDetected: handleOnSubmit,
    isDisabled: isScanningDisabled,
  });

  const isValid = !error && !!textFieldValue;

  const clear = (): void => {
    setTextFieldValue('');
    setError(undefined);
  };

  const submitTextField = (): void => {
    handleOnSubmit(textFieldValue);
  };

  const handleOnTextFieldKeydown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const wasEnterPressed = event.key === 'Enter';
    if (wasEnterPressed && isValid) {
      handleOnSubmit(textFieldValue);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setTextFieldValue(value);

    const errorMessages: string[] = [];
    if (value) {
      barcodeTypes.forEach(type => {
        const { validate } = barcodeFormatConfigByType[type];
        const validationErrorMessage = validate(value);
        if (!!validationErrorMessage && !errorMessages.includes(validationErrorMessage)) {
          errorMessages.push(validationErrorMessage);
        }
      });

      if (errorMessages.length === barcodeTypes.length) {
        setError(
          errorMessages.length > 1 ? barcodeInputTexts.dialog.alertError.unsupportedFormat.text : errorMessages[0],
        );
      } else {
        setError(undefined);
      }
    } else {
      setError(undefined);
    }
  };

  return {
    handleOnTextFieldKeydown,
    handleOnChange,
    submitTextField,
    textFieldValue,
    error,
    isScanningAvailable: isBrowserTabActive && !isScanningDisabled,
    isValid,
  };
};
